import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import apis from "services";
import TransactionTable from "../../components/datagrid/TransactionTable";
import { DataGrid } from "@mui/x-data-grid";
import AdminNewSidebar from "components/admin/AdminNewSidebar";
import { Header } from "components";
import Container from "@mui/material/Container";
import DeleteModal from "components/modals/DeleteModal";
import { toast } from "react-toastify";
import { Form, Button, Col } from "react-bootstrap";
import { CiSearch } from "react-icons/ci";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const PAGE_LIMIT = 20;

const TYPE_OPTIONS = [
  { label: "SPEI/STP", type: "card" },
  { label: "Donation", type: "donation" },
  { label: "Reinvest", type: "user-wallet" },
  { label: "Investment Via Admin", type: "wallet" },
  { label: "Offer", type: "offer" },
  { label: "Conekta Card", type: "conekta_card" },
  { label: "Conekta Bank Transfer", type: "conekta_bank_transfer" },
  { label: "Conekta Cash", type: "conekta_cash" },
  { label: "Oxxo/Femsa Cash", type: "femsa_cash" },
];

const formatMoney = (value) =>
  typeof value === "number"
    ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`
    : "$0.00 MXN";

const Transactions = () => {
  const [activeType, setActiveType] = useState(TYPE_OPTIONS[0].type);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({
    totalInvested: 0,
    totalReceived: 0,
    totalDividendRemainder: 0,
    totalShares: 0,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: PAGE_LIMIT,
    totalPages: 1,
  });

  const [show, setShow] = useState(false);
  const [loader, setLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transactionId, setTransactionId] = useState(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apis.getAdminTransactionHistory({
        type: activeType,
        search,
        page,
        limit: PAGE_LIMIT,
      });
      if (data?.status) {
        setTransactions(data.transactions || []);
        setSummary(data.summary || {
          totalInvested: 0,
          totalReceived: 0,
          totalDividendRemainder: 0,
          totalShares: 0,
        });
        setPagination(
          data.pagination || { total: 0, page, limit: PAGE_LIMIT, totalPages: 1 }
        );
      }
    } finally {
      setLoading(false);
    }
  }, [activeType, search, page]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleClose = () => {
    setShow(false);
    setTransactionId("");
    setLoader(false);
  };

  const deleteHandler = async (id) => {
    setLoader(true);

    const res = await apis.adminDeleteTransactionUser(id);
    if (res.data.status) {
      toast.success(res.data.message);
      await fetchTransactions();
    } else {
      toast.error(res.data?.message);
    }
    handleClose();
    setLoader(false);
  };

  const openModal = (id) => {
    setTransactionId(id);
    setShow(true);
  };

  const handleTypeClick = (type) => {
    setActiveType(type);
    setPage(1);
  };

  const onPageChange = (direction) => {
    if (direction === "prev" && page === 1) return;
    if (direction === "next" && page >= pagination.totalPages) return;

    setPage((currentPage) => (direction === "next" ? currentPage + 1 : currentPage - 1));
  };

  const tables = TransactionTable(transactions, { openModal });

  return (
    <Box className="dashboard-main">
      <AdminNewSidebar />
      <div className="dashboard-section-main">
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: "100vh",
            overflow: "auto",
          }}
          className="custom-scroll"
        >
          <Header />
          {loading ? (
            <div className="loader-container">
              <div className="loader" />
            </div>
          ) : (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }} className="mainContainerComp">
              <Col xs={12} md={6} lg={4} className="mt-3 p-0">
                <Form.Group className="my-2" style={{ position: "relative" }}>
                  <Form.Label>Search by User Name or Store Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter user name or store name"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  <div className="searchCon">
                    <CiSearch className="search-icon" color="#AFAEAE" fontSize={18} />
                  </div>
                </Form.Group>
              </Col>

              <div className="share-type d-flex justify-content-center flex-wrap mt-2">
                {TYPE_OPTIONS.map((option) => (
                  <Button
                    key={option.type}
                    className={activeType === option.type ? "w-10 active_type " : "w-10"}
                    variant="outline-danger"
                    onClick={() => handleTypeClick(option.type)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>

              <Grid container spacing={3} className="mt-3">
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: 3, border: "1px solid rgba(15, 23, 42, 0.08)" }}>
                    <CardContent sx={{ py: 3 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}
                      >
                        Total Amount Invested
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 1 }}>
                        {formatMoney(summary.totalInvested)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: 3, border: "1px solid rgba(15, 23, 42, 0.08)" }}>
                    <CardContent sx={{ py: 3 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}>
                        Total Amount Received
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 1 }}>
                        {formatMoney(summary.totalReceived)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: 3, border: "1px solid rgba(15, 23, 42, 0.08)" }}>
                    <CardContent sx={{ py: 3 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}>
                        Added to Dividends
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 1 }}>
                        {formatMoney(summary.totalDividendRemainder)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: 3, border: "1px solid rgba(15, 23, 42, 0.08)" }}>
                    <CardContent sx={{ py: 3 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}
                      >
                        Total Purchased Shares
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 1 }}>
                        {summary.totalShares || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid
                item
                container
                className="mt-4"
                justifyContent="flex-end"
                sx={{ height: "600px" }}
              >
                <DataGrid
                  rows={tables?.rows}
                  columns={tables?.columns}
                  hideFooter
                  pageSize={PAGE_LIMIT}
                  rowsPerPageOptions={[PAGE_LIMIT]}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                  }
                />
              </Grid>

              <div className="pagination-container">
                <div>Total: {pagination.total}</div>
                <div>
                  <button
                    className="paginator"
                    disabled={page === 1}
                    onClick={() => onPageChange("prev")}
                  >
                    <IoIosArrowBack />
                  </button>
                  Page {pagination.page} of {pagination.totalPages}
                  <button
                    className="paginator"
                    disabled={page >= pagination.totalPages}
                    onClick={() => onPageChange("next")}
                  >
                    <IoIosArrowForward />
                  </button>
                </div>
              </div>

              <DeleteModal
                show={show}
                handleClose={handleClose}
                handleSubmit={deleteHandler}
                loader={loader}
                id={transactionId}
                title={"Delete Transaction"}
              />
            </Container>
          )}
        </Box>
      </div>
    </Box>
  );
};

export default Transactions;
